import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  SingleFileUploader,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, { MIXPANEL_SOURCE, MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import { LegacyDocumentType } from '@commenda/prisma';
import { FileBasic } from '@commenda/types/src/file/types';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import type { Filing } from '../types';

interface CompleteFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing: Filing;
  onComplete: (filingId: string, file: FileBasic) => Promise<boolean>;
  onSuccess: () => void;
  companyId: number;
}

export const CompleteFilingModal = ({
  isOpen,
  onClose,
  filing,
  onComplete,
  onSuccess,
  companyId,
}: CompleteFilingModalProps) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  const isSubmitting = useToggle(false);
  const { setNotification } = useNotification();

  const close = () => {
    setFile(null);
    onClose();
  };

  const handleComplete = async () => {
    if (!file) return;

    isSubmitting.on();
    const success = await onComplete(filing.id, file);

    if (!success) {
      setNotification({
        title: 'Error',
        message: 'Failed to complete filing',
        valid: false,
        show: true,
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      title: 'Success',
      message: 'Filing has been marked as completed.',
      valid: true,
      show: true,
    });

    mixpanel.track('OBLIGATION_ACKNOWLEDGEMENT_UPLOADED', {
      source: MIXPANEL_SOURCE.CALENDAR,
      surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
      company_id: companyId,
      compliance_id: filing.id,
    });

    await onSuccess();
    isSubmitting.off();
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0 pb-0">
          <DialogTitle>Complete this filing</DialogTitle>
          <DialogDescription>
            If you have completed this filing already, upload a receipt to mark
            it as complete. Your accounting partner will be notified.
            <br />
            <br />
            Once completed, this action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Upload receipt</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              documentType={LegacyDocumentType.Other}
              file={file}
              setFile={setFile}
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button variant="secondary" onClick={close} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleComplete}
            size="md"
            disabled={!file || isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
